import type { PlatformLocation } from "@/lib/restaurant-platform";

export type MarketingRestaurantCopy = {
  name: string;
  shortName: string;
  phoneDisplay: string;
  phoneHref: string;
  emailPopup: MarketingEmailPopupCopy;
  incentivePopup: MarketingIncentivePopupCopy;
  privateEventsTitle: string;
  privateEventsIntro: string;
  cateringTitle: string;
  cateringIntro: string;
  jobsTitle: string;
  jobsIntro: string;
  smsConsentLabel: string;
  communicationsConsentLabel: string | null;
};

export type MarketingEmailPopupCopy = {
  source: "website" | "cove_club" | "roma_club";
  analyticsLocation: string;
  badge: string;
  dialogTitle: string;
  dialogDescription: string;
  imageAlt: string;
  heading: string;
  description: string;
  highlights: [string, string, string];
  submitLabel: string;
  successHeading: string;
  successDescription: string;
  successMenuCtaLabel: string;
  secondaryHref: string;
  secondaryLabel: string;
};

export type MarketingIncentivePopupCarouselItem = {
  name: string;
  /** Optional price badge on carousel slides; omit to hide prices. */
  valueText?: string;
  image: string;
  objectPosition?: string;
};

export type MarketingIncentivePopupCopy = {
  analyticsLocation: string;
  dialogTitle: string;
  dialogDescription: string;
  carouselItems: readonly MarketingIncentivePopupCarouselItem[];
  step1Eyebrow: string;
  step1Heading: string;
  step1HeadingEmphasis: string;
  step1Description: string;
  step1CtaLabel: string;
  step1DeclineLabel: string;
  step1TermsLine: string;
  formEyebrow: string;
  formHeading: string;
  formDescription: string;
  thanksEyebrow: string;
  thanksHeading: string;
  thanksDescription: string;
  thanksMenuCtaLabel: string;
  /** Optional "Up to $X" badge; omit to hide price caps in the popup. */
  valueCapText?: string;
};

export type MarketingSignalCopy = {
  label: string;
  value: string;
};

export type MarketingGuideLinkCopy = {
  href: string;
  label: string;
  ariaLabel: string;
  description: string;
};

export type MarketingFaqCopy = {
  question: string;
  answer: string;
};

export type MarketingReviewCopy = {
  eyebrow: string;
  excerpt: string;
  source: string;
  meta?: string;
};

export type MarketingHomepageBrand =
  | "tuscan-cove"
  | "chefs-roma"
  | "fia-restaurant";

export type RomaLocationCopy = {
  slug: "henderson" | "southern-highlands";
  eyebrow: string;
  cityLabel: string;
  name: string;
  shortName: string;
  addressLine1: string;
  addressLine2: string;
  phoneDisplay: string;
  phoneHref: string;
  hoursLabel: string;
  hoursDetail: string;
  noteLine: string;
  reserveUrl: string;
  grubhubUrl: string;
  mapsUrl: string;
  imageSrc: string;
  imageAlt: string;
  // Source: Google Business Profile API, verified 2026-08-03.
  // Refresh with the `gbp` CLI against the location ids recorded on each entry
  // below; the same verified pair is mirrored in `src/lib/restaurant-platform.ts`
  // (`verifiedAggregateRating`), which is what feeds Restaurant JSON-LD.
  googleRating: number;
  googleReviewCount: number;
};

export type MarketingPrivateDiningCopy = {
  eyebrow: string;
  title: string;
  description: string;
  bullets: readonly string[];
  ctaLabel: string;
  ctaHref: string;
  secondaryCtaLabel: string;
  secondaryCtaHref: string;
  imageSrc: string;
  imageAlt: string;
  imageCaption: string;
};

export type MarketingServiceRhythmItem = {
  label: string;
  value: string;
};

export type MarketingPickupRowCopy = {
  eyebrow: string;
  heading: string;
  description: string;
  options: readonly {
    label: string;
    meta: string;
    href: string;
  }[];
};

export type MarketingHomepageCopy = {
  brand: MarketingHomepageBrand;
  metadataTitle: string;
  metadataDescription: string;
  keywords: string[];
  mobileBrandLabel: string;
  mobileHeroTitle: string;
  mobileHeroDescription: string;
  heroImageAlt: string;
  desktopHeroImageAlt: string;
  eyebrow: string;
  heroTitle: string;
  answerFirst: string;
  primaryCtaLabel: string;
  secondaryCtaLabel: string;
  quickStats: MarketingSignalCopy[];
  photoEyebrow: string;
  photoTitle: string;
  photoMetaAddress: string;
  photoMetaStatus: string;
  quickStripMenuDescription: string;
  directionsAddress: string;
  groupsDescription: string;
  hoursQuickLabel?: string;
  hoursQuickValue?: string;
  phoneCtaLabel: string;
  phoneCtaDescription: string;
  guestSectionEyebrow: string;
  guestSectionTitle: string;
  guestSectionDescription: string;
  guestImageAlt: string;
  guestImageCaption: string;
  guestSignals: MarketingSignalCopy[];
  reviewsHeading: string;
  reviewsHref: string;
  featuredReviews: MarketingReviewCopy[];
  happyHour: {
    eyebrow: string;
    title: string;
    description: string;
    hours: string;
    imageAlt: string;
    imageEyebrow: string;
    imageDescription: string;
    details: MarketingSignalCopy[];
  };
  inlineOffer: {
    source: "website" | "cove_club" | "roma_club";
    eyebrow: string;
    heading: string;
    description: string;
    submitLabel: string;
    privacyNote: string;
    successEyebrow: string;
    successHeading: string;
    successDescription: string;
  };
  menuPreviewDescription: string;
  featureHeading: string;
  featureBadges: string[];
  knownForItems: string[];
  orderEyebrow: string;
  orderHeading: string;
  orderDescription: string;
  orderCtaLabel: string;
  localGuidesEyebrow: string;
  localGuidesTitle: string;
  localGuideLinks: MarketingGuideLinkCopy[];
  visitEyebrow: string;
  visitTitle: string;
  visitGateCode?: string;
  visitDirectionsCtaLabel?: string;
  visitSignAlt: string;
  visitSignCaption: string;
  visitMapAlt: string;
  visitMapEyebrow: string;
  visitMapDescription: string;
  atAGlanceTitle: string;
  atAGlanceDescription: string;
  atAGlancePhoneLabel: string;
  faqItems: MarketingFaqCopy[];
  romaLocations?: readonly RomaLocationCopy[];
  romaLocationsHeading?: string;
  romaLocationsEyebrow?: string;
  romaLocationsIntro?: string;
  privateDining?: MarketingPrivateDiningCopy;
  serviceRhythm?: readonly MarketingServiceRhythmItem[];
  pickupRow?: MarketingPickupRowCopy;
  // Order-first funnel (Tuscan). Honest qualitative proof line seated above the
  // hero headline (no fabricated rating number), a friction-reducing assurance line
  // under the hero CTAs, and the copy for the post-menu ordering band. All
  // optional so the Roma block is unaffected.
  heroSocialProof?: string;
  heroReserveAssurance?: string;
  heroOrderLinkLabel?: string;
  reserveReinforcement?: {
    eyebrow: string;
    title: string;
    description: string;
    ctaLabel: string;
  };
};

export function formatPhone(phone: string): string {
  const digits = phone.replace(/\D/g, "");
  const normalized =
    digits.length === 11 && digits.startsWith("1") ? digits.slice(1) : digits;

  if (normalized.length !== 10) {
    return phone;
  }

  return `(${normalized.slice(0, 3)}) ${normalized.slice(3, 6)}-${normalized.slice(6)}`;
}

function shortNameForLocation(location: PlatformLocation): string {
  return location.name === "Tuscan Cove Bar & Patio"
    ? "Tuscan Cove"
    : location.name;
}

function addressLine(location: PlatformLocation): string {
  return `${location.address.line1}, ${location.address.city}, ${location.address.state} ${location.address.zip}`;
}

const TUSCAN_INCENTIVE_POPUP: MarketingIncentivePopupCopy = {
  analyticsLocation: "cove_club_incentive_popup",
  dialogTitle: "Cove Club membership offer",
  dialogDescription:
    "Join the Cove Club and claim a free appetizer coupon from Tuscan Cove.",
  carouselItems: [
    {
      name: "Wings",
      image: "/images/menu/tuscan-cove-chicken-wings.jpg",
      objectPosition: "center 55%",
    },
    {
      name: "Tenders",
      image: "/images/menu/tuscan-cove-chicken-tenders.jpg",
      objectPosition: "center 45%",
    },
    {
      name: "Fries",
      image: "/images/menu/tuscan-cove-basket-of-fries.jpg",
      objectPosition: "center",
    },
  ],
  step1Eyebrow: "Cove Club · Members only",
  step1Heading: "Join the Cove Club.",
  step1HeadingEmphasis: "Your first appetizer's on us.",
  step1Description:
    "Member events, tastings, and specials we never post, plus a free welcome appetizer.",
  step1CtaLabel: "Claim my free appetizer",
  step1DeclineLabel: "No thanks",
  step1TermsLine: "Free to join · with entrée · one per guest",
  formEyebrow: "Takes 10 seconds",
  formHeading: "Claim your welcome gift",
  formDescription:
    "Coupon arrives by email in ~1 minute. No spam, unsubscribe anytime.",
  thanksEyebrow: "You're in",
  thanksHeading: "Welcome to the Cove Club.",
  thanksDescription:
    "Your coupon is on its way. Check your inbox. If it doesn't land in a minute, peek at spam or promotions.",
  thanksMenuCtaLabel: "Browse the menu",
};

const ROMA_INCENTIVE_POPUP: MarketingIncentivePopupCopy = {
  analyticsLocation: "roma_club_incentive_popup",
  dialogTitle: "Roma Club membership offer",
  dialogDescription:
    "Join Roma Club and claim a free antipasto coupon at Chef's Roma Kitchen in Southern Highlands or Henderson.",
  carouselItems: [
    {
      name: "Meatballs",
      valueText: "$12",
      image: "/images/menu/roma/35-meatballs.jpg",
      objectPosition: "center",
    },
    {
      name: "Stuffed Artichokes",
      valueText: "$16",
      image: "/images/menu/roma/36-stuffed-artichokes.jpg",
      objectPosition: "center",
    },
    {
      name: "Fried Calamari",
      valueText: "$19",
      // No local Roma calamari photo exists under public/images/menu/ yet, so
      // we keep this hosted SpotApps image. Swap to a local /images/menu/ path
      // once a licensed Roma calamari shot is added.
      image:
        "https://static.spotapps.co/spots/76/1fe4bd1e414ebd8cb4c9e9aeff1556/full",
      objectPosition: "center",
    },
  ],
  step1Eyebrow: "Roma Club · Exclusive membership",
  step1Heading: "Join Roma Club.",
  step1HeadingEmphasis: "Your first antipasto is on us.",
  step1Description:
    "Member tastings, pasta, and dinner notes we never post, plus a welcome antipasto up to $20 at both locations.",
  step1CtaLabel: "Yes, send my free antipasto",
  step1DeclineLabel: "Maybe later",
  step1TermsLine:
    "Free to join. Welcome antipasto with purchase of an entrée, one per table, both locations.",
  formEyebrow: "Roma Club",
  formHeading: "Claim your welcome gift",
  formDescription:
    "Coupon arrives by email in ~1 minute. One per table. No spam, unsubscribe anytime.",
  thanksEyebrow: "You're in",
  thanksHeading: "Welcome to Roma Club.",
  thanksDescription:
    "Your coupon is on the way. Check your inbox. If it doesn't land in a minute, peek at spam or promotions.",
  thanksMenuCtaLabel: "Browse the menu",
  valueCapText: "$20",
};

export function buildRestaurantCopy(
  location: PlatformLocation,
): MarketingRestaurantCopy {
  const shortName = shortNameForLocation(location);
  const isRoma = location.slug === "roma-kitchen-southern-highlands";
  const isTuscan = location.slug === "tuscan-cove-southern-highlands";

  return {
    name: location.name,
    shortName,
    phoneDisplay: formatPhone(location.contact.phone),
    phoneHref: location.contact.phone.replace(/[^\d+]/g, ""),
    incentivePopup: isRoma ? ROMA_INCENTIVE_POPUP : TUSCAN_INCENTIVE_POPUP,
    emailPopup: isRoma
      ? {
          source: "roma_club",
          analyticsLocation: "roma_club_popup",
          badge: "Roma Club",
          dialogTitle: "Roma Club Signup",
          dialogDescription:
            "Join the free Roma Club exclusive membership for chef's tasting nights, pasta, and member-only specials.",
          imageAlt: "Roma Kitchen Italian dishes and dining room",
          heading:
            "Chef's tastings, pasta, and dinner notes. Hear about them first.",
          description:
            "Free to join. Get chef's tasting nights, holiday tables, private-dining notes, and first access to member-only specials by email.",
          highlights: [
            "Exclusive membership",
            "Chef's tastings",
            "Both locations",
          ],
          submitLabel: "Join Roma Club",
          successHeading: "You are in Roma Club.",
          successDescription:
            "Your exclusive membership is confirmed. Watch your inbox for chef's tasting nights, pasta, and member-only specials when they go live.",
          successMenuCtaLabel: "View the Menu",
          secondaryHref: "/private-events",
          secondaryLabel: "Plan a private event",
        }
      : {
          source: "cove_club",
          analyticsLocation: "cove_club_popup",
          badge: "Cove Club",
          dialogTitle: "Cove Club Signup",
          dialogDescription:
            "Join the free Cove Club membership for early access to events, patio nights, happy hour notes, and member-only specials.",
          imageAlt: "Cucumber mint mule cocktail at the Tuscan Cove bar",
          heading:
            "Patio nights, happy hour, and game days. Hear about them first in Southern Highlands.",
          description:
            "Free to join. Get patio night invites, happy hour notes, game day reminders, and first access to member-only specials by email.",
          highlights: ["Free to join", "Patio nights", "Game days"],
          submitLabel: "Join Cove Club",
          successHeading: "You are in Cove Club.",
          successDescription:
            "Your free membership is confirmed. Watch your inbox for patio nights, happy hour notes, and member-only specials when they go live.",
          successMenuCtaLabel: "View the Menu",
          secondaryHref: "/events",
          secondaryLabel: "See upcoming events",
        },
    privateEventsTitle: isRoma
      ? "Private dining in Las Vegas, Southern Highlands, and Henderson"
      : "Host your event at Tuscan Cove",
    privateEventsIntro: isRoma
      ? "Tell us a few details and the Roma team will help with private dining, group dining, rehearsal dinners, corporate meals, and celebrations in Southern Highlands or Henderson."
      : "Tell us a few details and we'll help with the next step.",
    cateringTitle: isRoma
      ? "Italian catering in Las Vegas and Henderson"
      : "Order catering from Tuscan Cove",
    cateringIntro: isRoma
      ? "Use the same quick catering request flow for pasta trays, office meals, pickup, delivery notes, and group meals across Southern Highlands and Henderson."
      : "Tell us a few details and we'll help with trays, pickup, or delivery.",
    jobsTitle: isRoma ? "Jobs at Chef's Roma Kitchen" : `Jobs at ${shortName}`,
    jobsIntro: isRoma
      ? "Send a quick application for the Roma restaurant team in Southern Highlands or Henderson. Resume optional."
      : "Send a quick application for the restaurant team. Resume optional.",
    smsConsentLabel: `I agree to receive event and catering text messages from ${location.name}. Message frequency varies. Message and data rates may apply. Reply STOP to opt out, HELP for help. Consent is not required to submit this request.`,
    communicationsConsentLabel: isTuscan
      ? "I agree to receive an automated call using an AI-generated voice and event-related text messages from Tuscan Cove Bar & Patio at the number provided about this inquiry. Message frequency varies. Message and data rates may apply. Reply STOP to opt out of texts or ask us not to call. Consent is not required to submit this request."
      : null,
  };
}

export function buildHomepageCopy(
  location: PlatformLocation,
): MarketingHomepageCopy {
  const isRoma = location.slug === "roma-kitchen-southern-highlands";
  const isFia = location.slug === "fia-restaurant-santa-monica";
  const formattedPhone = formatPhone(location.contact.phone);
  const locationAddress = addressLine(location);

  if (isRoma) {
    const hendersonGrubhubUrl =
      "https://www.grubhub.com/search?searchTerm=Chef%27s%20Roma%20Kitchen&location=1550%20W%20Horizon%20Ridge%20Pkwy%2C%20Henderson%2C%20NV%2089012";
    const hendersonMapsUrl =
      "https://www.google.com/maps/search/?api=1&query=" +
      encodeURIComponent(
        "Chef's Roma Kitchen 1550 W Horizon Ridge Pkwy Henderson NV 89012",
      );
    const southernHighlandsMapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
      `${location.name} ${locationAddress}`,
    )}`;

    return {
      brand: "chefs-roma",
      metadataTitle:
        "Chef's Roma Kitchen | Italian Restaurants in Las Vegas & Henderson",
      metadataDescription:
        "Reserve Chef's Roma Kitchen for pasta, lamb chops, Italian wine, private dining, and catering at Southern Highlands or Henderson.",
      keywords: [
        "Chef's Roma Kitchen",
        "Chef's Roma Kitchen Henderson",
        "Roma Kitchen Las Vegas",
        "Roma Kitchen Southern Highlands",
        "Roma Kitchen Henderson",
        "Italian restaurant Southern Highlands",
        "Italian restaurant Henderson NV",
        "Italian private dining Las Vegas",
      ],
      mobileBrandLabel: "Chef's Roma Kitchen",
      mobileHeroTitle: "Italian dinner in Las Vegas and Henderson.",
      mobileHeroDescription:
        "Pasta, lamb chops, and Italian wine at Southern Highlands and Henderson. Reserve a table, or ask about private dining and catering.",
      heroImageAlt:
        "Plated Roma Kitchen lamb chops with roasted potatoes and asparagus",
      desktopHeroImageAlt:
        "Plated Roma Kitchen lamb chops with roasted potatoes and asparagus",
      eyebrow: "Chef's Roma Kitchen · Southern Highlands + Henderson",
      heroTitle: "Chef-led Italian dinner in Las Vegas and Henderson.",
      answerFirst:
        "Pasta, lamb chops, and Italian wine at Southern Highlands and Henderson. Reserve a table, or ask about private dining and catering.",
      primaryCtaLabel: "Reserve a table",
      secondaryCtaLabel: "View the menu",
      quickStats: [
        {
          label: "Dinner service",
          value: "Daily from 4pm",
        },
        {
          label: "Two locations",
          value: "Southern Highlands and Henderson",
        },
        {
          label: "Private dining",
          value: "Rehearsals, office meals, catering requests",
        },
      ],
      photoEyebrow: "Tonight, at the table",
      photoTitle: "Pasta, wine, lamb chops, and a room built for dinner.",
      photoMetaAddress: "10420 S Decatur Blvd #100",
      photoMetaStatus: "Reserve online with Google Reserve",
      quickStripMenuDescription:
        "Antipasti, pasta, lamb chops, charred mains, and Italian wine by the glass or bottle.",
      directionsAddress: "10420 S Decatur Blvd #100",
      groupsDescription: "Private dining and rehearsal dinners",
      phoneCtaLabel: "Call Roma Kitchen",
      phoneCtaDescription: `${formattedPhone} for reservations, private dining, and the chef's tasting questions.`,
      guestSectionEyebrow: "Why people return",
      guestSectionTitle:
        "A neighborhood Italian dining room with a chef's touch.",
      guestSectionDescription:
        "Chef's Roma Kitchen keeps dinner focused: warm service, pasta, classic mains, and enough room for a date night, family table, or small celebration.",
      guestImageAlt: "Roma Kitchen Italian dining room with warm lighting",
      guestImageCaption: "An unhurried Italian dinner, exactly as advertised.",
      guestSignals: [
        {
          label: "The kitchen",
          value:
            "Pasta, lamb chops, Italian classics, and chef's tasting notes when the kitchen has something special.",
        },
        {
          label: "The room",
          value:
            "Southern Highlands and Henderson tables with warm lighting, real conversation, and reservations honored by location.",
        },
        {
          label: "The list",
          value:
            "Italian wine by the glass, bottles for the table, and pours selected to work with the food.",
        },
      ],
      reviewsHeading: "What guests notice about Roma",
      reviewsHref: `https://www.google.com/search?q=${encodeURIComponent(
        `${location.name} ${location.address.line1} reviews`,
      )}`,
      featuredReviews: [
        {
          eyebrow: "The pasta",
          excerpt:
            "Pasta, careful sauces, lamb chops, and Italian wine make Roma an easy repeat dinner.",
          source: "Roma diners",
          meta: "Southern Highlands",
        },
        {
          eyebrow: "Private dining",
          excerpt:
            "Rehearsal dinners, anniversaries, and small business dinners all settle into Roma the same easy way.",
          source: "Celebrations",
          meta: "Both locations",
        },
        {
          eyebrow: "The wine list",
          excerpt:
            "Italian wines by the glass and bottles for the table keep the meal moving without making it complicated.",
          source: "Roma diners",
          meta: "Henderson",
        },
        {
          eyebrow: "Catering requests",
          excerpt:
            "Office lunches, home dinners, and hosted celebrations start with the same direct Roma team.",
          source: "Roma planning",
          meta: "Events",
        },
      ],
      happyHour: {
        eyebrow: "Dinner service",
        title: "Antipasti, pasta, lamb chops, and Italian wine.",
        description:
          "Start with a board, pour something Italian, order the pasta, then move into lamb chops, seafood, or the chef's tasting when it is offered.",
        hours: "From 4pm daily",
        imageAlt: "Roma Kitchen Italian dining room with antipasti and wine",
        imageEyebrow: "On the table",
        imageDescription:
          "A reason to slow down for a glass of something Italian, pasta, and a proper entree.",
        details: [
          {
            label: "On the table",
            value: "Antipasti boards, pasta, charred mains",
          },
          {
            label: "On the list",
            value: "Italian wine, cocktails, and a small grappa selection",
          },
        ],
      },
      inlineOffer: {
        source: "roma_club",
        eyebrow: "Roma Club · Exclusive membership",
        heading: "Member nights. Better seats.",
        description:
          "Join free. Get the drop on chef tastings, pasta specials, and holiday bookings before they fill.",
        submitLabel: "Join Roma Club",
        privacyNote:
          "Email is used for Roma Club updates. Optional promotional texts only if you check the SMS box above. Free to join. Unsubscribe anytime. Privacy: chefsromakitchen.com/privacy.",
        successEyebrow: "You are in Roma Club",
        successHeading: "Watch your inbox for Roma Club updates.",
        successDescription:
          "Your exclusive membership is confirmed. Watch your inbox for chef's tasting nights, pasta, holiday tables, and member-only specials when they go live.",
      },
      menuPreviewDescription:
        "A handful of our most-ordered dishes. The chef's tasting follows the season.",
      featureHeading: "A look at dinner at Roma.",
      featureBadges: [
        "Chef-led Italian",
        "Pasta",
        "Lamb Chops",
        "Yelp Reservations",
        "Private Dining",
        "Catering",
      ],
      knownForItems: [
        "Italian classics",
        "Pasta",
        "Lamb chops",
        "Charred meats",
        "Italian wine",
        "Private Dining",
        "Celebrations",
      ],
      orderEyebrow: "Pickup + delivery",
      orderHeading: "Order Chef's Roma Kitchen on Grubhub.",
      orderDescription:
        "Pickup and delivery, when available, are handled outside the first-party site through Grubhub.",
      orderCtaLabel: "Order on Grubhub",
      localGuidesEyebrow: "Roma at a glance",
      localGuidesTitle: "Pick the next step.",
      localGuideLinks: [
        {
          href: location.menuUrl,
          label: "Menu",
          ariaLabel: "View the Roma Kitchen menu",
          description:
            "Antipasti, pasta, lamb chops, charred mains, and the wine list.",
        },
        {
          href: "/private-events",
          label: "Private Dining",
          ariaLabel: "Request a Roma Kitchen private dinner",
          description:
            "Rehearsals, anniversaries, business dinners, and celebrations.",
        },
        {
          href: "/catering",
          label: "Catering",
          ariaLabel: "Request Roma Kitchen catering",
          description:
            "Italian boards and entrees for offices, homes, and gatherings.",
        },
        {
          href: "/jobs",
          label: "Work with us",
          ariaLabel: "Apply to work at Roma Kitchen",
          description:
            "Front of house and kitchen positions at both locations.",
        },
        {
          href: southernHighlandsMapsUrl,
          label: "Directions",
          ariaLabel: "Get directions to Roma Kitchen",
          description: "10420 S Decatur Blvd #100, Las Vegas, NV 89141.",
        },
      ],
      visitEyebrow: "Two locations, one Roma",
      visitTitle: "Visit the Roma table closest to you.",
      visitSignAlt:
        "Roma Kitchen exterior at 10420 S Decatur Blvd in Las Vegas",
      visitSignCaption: "Roma Kitchen, 10420 S Decatur Blvd #100, Las Vegas.",
      visitMapAlt:
        "Roma Kitchen dining room interior with warm Italian lighting",
      visitMapEyebrow: "On the table",
      visitMapDescription:
        "Pasta, antipasti, pour, and the quiet Italian dinner.",
      atAGlanceTitle:
        "Chef's Roma Kitchen in Southern Highlands and Henderson.",
      atAGlanceDescription:
        "Chef's Roma Kitchen runs two locations in the Las Vegas valley: the Henderson original on Horizon Ridge Parkway and the Southern Highlands dining room on S Decatur Blvd. Reservations use Google Reserve at each location; private dining and tasting menus are handled by the team direct.",
      atAGlancePhoneLabel:
        "for reservations, private dining, and tasting-menu questions",
      faqItems: [
        {
          question: "Where are the Chef's Roma Kitchen locations?",
          answer:
            "Chef's Roma Kitchen has two locations in the Las Vegas valley: Southern Highlands at 10420 S Decatur Blvd #100, Las Vegas, NV 89141, and Henderson at 1550 W Horizon Ridge Pkwy Ste P, Henderson, NV 89012.",
        },
        {
          question: "How do I reserve a table at Roma Kitchen?",
          answer:
            "Reservations for both Roma locations use Google Reserve. Call Southern Highlands at (702) 888-3414 or Henderson at (702) 331-6300 for tasting menus, timing, and private dining questions.",
        },
        {
          question: "Which Roma location should I choose?",
          answer:
            "Choose Southern Highlands for the S Decatur dining room near southwest Las Vegas, or Henderson for the Horizon Ridge original. Both locations accept reservations through Google Reserve and can point private dining questions to the Roma team.",
        },
        {
          question: "Can I order Roma Kitchen for pickup or delivery?",
          answer:
            "Pickup and delivery, when available, route through Grubhub. Reservations, private dining, catering, and job inquiries are handled through the Roma website and direct team.",
        },
        {
          question: "Does Roma Kitchen offer private dining or celebrations?",
          answer:
            "Yes. Roma hosts rehearsal dinners, anniversaries, business dinners, birthdays, and small celebrations at both locations. Use the private dining request to share the date, group size, and any chef's tasting requests.",
        },
        {
          question: "How fast does the Roma team reply?",
          answer:
            "Private dining and catering requests go through the direct Roma inquiry path. If the date is urgent or same-week, call the preferred location after submitting so the team can confirm availability faster.",
        },
        {
          question: "How do I contact Roma Kitchen?",
          answer: `Call Southern Highlands at ${formattedPhone}, Henderson at (702) 331-6300, or use the contact form for catering and private dining inquiries.`,
        },
      ],
      romaLocationsEyebrow: "Southern Highlands + Henderson",
      romaLocationsHeading: "Two locations, one wine list.",
      romaLocationsIntro:
        "Pick the table closest to you. Each location runs its own evening; both pour from the same list.",
      romaLocations: [
        {
          slug: "henderson",
          eyebrow: "Henderson",
          cityLabel: "Henderson, NV",
          name: "Chef's Roma Kitchen",
          shortName: "Roma Henderson",
          addressLine1: "1550 W Horizon Ridge Pkwy Ste P",
          addressLine2: "Henderson, NV 89012",
          phoneDisplay: "(702) 331-6300",
          phoneHref: "+17023316300",
          hoursLabel: "Open daily",
          hoursDetail: "Daily from 4pm",
          noteLine:
            "Original Roma off Horizon Ridge near McDonald Highlands. Late bar, regulars' dining room.",
          reserveUrl:
            "https://www.google.com/maps/reserve/v/dine/c/GYmLWTH3LNU",
          grubhubUrl: hendersonGrubhubUrl,
          mapsUrl: hendersonMapsUrl,
          imageSrc: "/images/chef-roma-kitchen-henderson-exterior.jpg",
          imageAlt:
            "Chef's Roma Kitchen exterior at dusk on Horizon Ridge Parkway in Henderson, with the warm glowing script signage",
          // Source: Google Business Profile API (locations/16277542901683087712),
          // verified 2026-08-03.
          googleRating: 4.7,
          googleReviewCount: 304,
        },
        {
          slug: "southern-highlands",
          eyebrow: "Southern Highlands",
          cityLabel: "Las Vegas, NV",
          name: "Roma Kitchen Southern Highlands",
          shortName: "Roma Southern Highlands",
          addressLine1: "10420 S Decatur Blvd #100",
          addressLine2: "Las Vegas, NV 89141",
          phoneDisplay: "(702) 888-3414",
          phoneHref: "+17028883414",
          hoursLabel: "Dinner service",
          hoursDetail: "Mon-Sat 4-9 PM; Sun 4-8 PM",
          noteLine:
            "S Decatur dining room. Quiet tables, chef-led Italian plates, parking at the door.",
          reserveUrl:
            "https://www.google.com/maps/reserve/v/dine/c/WuQwA1k3kus",
          grubhubUrl:
            "https://www.grubhub.com/restaurant/roma-kitchen-10420-south-decatur-boulevard-las-vegas/11247504",
          mapsUrl: southernHighlandsMapsUrl,
          imageSrc: "/images/roma-kitchen-exterior.jpg",
          imageAlt:
            "Roma Kitchen exterior on S Decatur Boulevard in Southern Highlands, Las Vegas, with white striped awnings",
          // Source: Google Business Profile API (locations/5937938347753510221),
          // verified 2026-08-03.
          googleRating: 4.7,
          googleReviewCount: 145,
        },
      ],
      privateDining: {
        eyebrow: "Private dining + catering",
        title: "Private dining and catering. Booked direct.",
        description:
          "Send the date, headcount, and style. Roma follows up for a hosted dinner, office meal, home catering, or tasting.",
        bullets: [
          "Up to 100 guests for hosted private dining",
          "Office and home catering requests",
          "Rehearsal dinners, birthdays, business dinners",
          "Chef-led menus and wine pairings",
        ],
        ctaLabel: "Request private dining",
        ctaHref: "/private-events",
        secondaryCtaLabel: "Catering for offices and homes",
        secondaryCtaHref: "/catering",
        imageSrc:
          "https://static.spotapps.co/website_images/ab_websites/101218_website/about_left_new.jpg",
        imageAlt:
          "Roma Kitchen private dining table with Italian place setting",
        imageCaption: "A long table, set the way you'd want it set at home.",
      },
      serviceRhythm: [
        { label: "Dinner", value: "From 4pm, every night" },
        { label: "Tasting", value: "Chef's menu most evenings" },
        { label: "Reservations", value: "Yelp, by location" },
        { label: "Private dining", value: "8 to 100 guests, by request" },
      ],
      pickupRow: {
        eyebrow: "External pickup",
        heading: "Pickup stays secondary for this launch.",
        description:
          "Roma's first-party site is focused on reservations, private dining, catering, and jobs. Pickup links remain available through Grubhub when guests need them.",
        options: [
          {
            label: "Grubhub from Henderson",
            meta: "Chef's Roma Kitchen, Horizon Ridge",
            href: hendersonGrubhubUrl,
          },
          {
            label: "Grubhub from Southern Highlands",
            meta: "Roma Kitchen, S Decatur Blvd",
            href: "https://www.grubhub.com/restaurant/roma-kitchen-10420-south-decatur-boulevard-las-vegas/11247504",
          },
        ],
      },
    };
  }

  if (isFia) {
    const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
      `${location.name} ${locationAddress}`,
    )}`;

    return {
      brand: "fia-restaurant",
      metadataTitle: "Fia Restaurant | Coastal Italian in Santa Monica",
      metadataDescription:
        "Fia Restaurant in Santa Monica serves coastal Italian dinner, weekend garden brunch, cocktails, wine, private events, reservations, and Toast pickup or delivery.",
      keywords: [
        "Fia Restaurant Santa Monica",
        "coastal Italian Santa Monica",
        "Santa Monica garden restaurant",
        "Fia Restaurant brunch",
        "Fia Restaurant reservations",
      ],
      mobileBrandLabel: "Fia Restaurant",
      mobileHeroTitle: "Coastal Italian in Santa Monica",
      mobileHeroDescription:
        "Dinner nightly, weekend garden brunch, and private events.",
      heroImageAlt: "Fia Restaurant garden dining room in Santa Monica",
      desktopHeroImageAlt:
        "Fia Restaurant interior and garden dining room in Santa Monica",
      eyebrow: "Fia Restaurant · Santa Monica",
      heroTitle: "A coastal Italian garden in Santa Monica.",
      answerFirst:
        "Fia pairs California's coast with Italian dinner, pasta, a lush garden setting, weekend brunch, wine, cocktails, and events at 2454 Wilshire Boulevard.",
      primaryCtaLabel: "Reserve a table",
      secondaryCtaLabel: "View the menu",
      quickStats: [
        {
          label: "Dinner",
          value: "Sun-Thu 5-9 PM, Fri-Sat 5-10 PM",
        },
        {
          label: "Weekend brunch",
          value: "Sat-Sun 10:30 AM-4:30 PM",
        },
        {
          label: "Ordering",
          value: "Toast pickup and delivery",
        },
      ],
      photoEyebrow: "Santa Monica",
      photoTitle: "A garden room built for dinner, brunch, and celebrations.",
      photoMetaAddress: location.address.line1,
      photoMetaStatus: "Reservations through Fia",
      quickStripMenuDescription:
        "Shared starters, crudo, pasta, coastal seafood, brunch, cocktails, and wine.",
      directionsAddress: location.address.line1,
      groupsDescription: "Private dining, events, brunch, and celebrations",
      hoursQuickLabel: "Hours",
      hoursQuickValue:
        "Dinner Sun-Thu 5-9pm, Fri-Sat 5-10pm · Brunch Sat-Sun 10:30am-4:30pm",
      phoneCtaLabel: "Call Fia",
      phoneCtaDescription: `${formattedPhone} for reservation help, dinner questions, and the Fia team.`,
      guestSectionEyebrow: "The Fia experience",
      guestSectionTitle: "A Santa Monica room made for a long table.",
      guestSectionDescription:
        "Guests come for the garden setting, shared plates, seafood, Wellington, cocktails, and a dining room that feels relaxed without losing polish.",
      guestImageAlt: "Fia Restaurant garden dining and lights",
      guestImageCaption:
        "Coastal Italian dinner in a lush Santa Monica setting.",
      guestSignals: [
        {
          label: "The room",
          value:
            "A garden-forward Santa Monica space with indoor and outdoor energy.",
        },
        {
          label: "The table",
          value:
            "Shared starters, raw fish, pasta, mains, brunch, and dessert.",
        },
        {
          label: "The next step",
          value:
            "Reserve through Fia, order through Toast, or contact events direct.",
        },
      ],
      reviewsHeading: "What guests notice about Fia",
      reviewsHref: location.yelpReservationUrl,
      featuredReviews: [
        {
          eyebrow: "The setting",
          excerpt:
            "A peaceful outdoor ambiance with string lights and a garden feel in Santa Monica.",
          source: "Fia guests",
          meta: "Santa Monica",
        },
        {
          eyebrow: "For the table",
          excerpt:
            "Chopped salad, raw fish dishes, Wellington steak, and chocolate desserts are frequent guest picks.",
          source: "Fia guests",
          meta: "Dinner",
        },
        {
          eyebrow: "Must-orders",
          excerpt:
            "Meatballs, brussels sprouts, burrata with fig, and shared plates lead the table.",
          source: "Fia guests",
          meta: "Menu",
        },
      ],
      happyHour: {
        eyebrow: "Dinner + brunch",
        title: "Coastal Italian plates with California ease.",
        description:
          "Move from starters and crudo into pasta, seafood, mains, dessert, cocktails, and wine.",
        hours: "Dinner nightly · brunch Saturday and Sunday",
        imageAlt:
          "Fia Restaurant interior with coastal Italian dining room details",
        imageEyebrow: "At the table",
        imageDescription:
          "Shared plates, garden seating, wine, cocktails, and a slower Santa Monica dinner.",
        details: [
          {
            label: "Dinner",
            value: "Shared starters, seafood, pasta, and mains",
          },
          {
            label: "Brunch",
            value: "Weekend garden brunch and mimosas",
          },
        ],
      },
      inlineOffer: {
        source: "website",
        eyebrow: "Fia updates",
        heading: "Dinner, brunch, and event notes from Fia.",
        description:
          "Seasonal menus, brunch updates, private dining, and special events from the Fia team.",
        submitLabel: "Join the list",
        privacyNote: "Email is used for Fia updates. Unsubscribe anytime.",
        successEyebrow: "You are on the Fia list",
        successHeading: "Watch your inbox for Fia updates.",
        successDescription:
          "You are signed up for Fia dinner, brunch, event, and private dining updates.",
      },
      menuPreviewDescription:
        "Representative items from Fia's dinner, brunch, dessert, Sunday Supper, wine, and cocktail menus.",
      featureHeading: "Reserve the garden, then let the table fill in.",
      featureBadges: [
        "Coastal Italian",
        "Garden Dining",
        "OpenTable-style Reservations",
        "Toast Ordering",
        "Private Events",
      ],
      knownForItems: [
        "Burrata & Fig",
        "Meatballs",
        "Crudo",
        "Wellington",
        "Brunch",
        "Cocktails",
        "Private Dining",
      ],
      orderEyebrow: "External ordering",
      orderHeading: "Order Fia through Toast.",
      orderDescription:
        "Pickup and delivery are handled outside the first-party site through Toast. No Tuscan Cove cart is used for Fia.",
      orderCtaLabel: "Order on Toast",
      localGuidesEyebrow: "Fia at a glance",
      localGuidesTitle: "Choose the next step.",
      localGuideLinks: [
        {
          href: "/menu",
          label: "Menu",
          ariaLabel: "View the Fia Restaurant menu",
          description:
            "Dinner, brunch, Sunday Supper, dessert, wine, and cocktails.",
        },
        {
          href: location.reservationUrl,
          label: "Reservations",
          ariaLabel: "Reserve a table at Fia Restaurant",
          description:
            "Table reservations through Fia's external reservation flow.",
        },
        {
          href: location.orderUrl,
          label: "Toast",
          ariaLabel: "Order Fia Restaurant through Toast",
          description: "External pickup and delivery ordering.",
        },
        {
          href: location.privatePartiesUrl ?? location.defaultBaseUrl,
          label: "Private Dining",
          ariaLabel: "Plan a private event at Fia Restaurant",
          description: "Garden celebrations, dinners, buyouts, and events.",
        },
        {
          href: mapsUrl,
          label: "Directions",
          ariaLabel: "Get directions to Fia Restaurant",
          description: `${location.address.line1}, ${location.address.city}, ${location.address.state} ${location.address.zip}.`,
        },
      ],
      visitEyebrow: "Visit Fia",
      visitTitle: "Find Fia on Wilshire Boulevard in Santa Monica.",
      visitDirectionsCtaLabel: "Open in Maps",
      visitSignAlt: "Fia Restaurant in Santa Monica",
      visitSignCaption: "2454 Wilshire Boulevard, Santa Monica.",
      visitMapAlt: "Fia Restaurant Santa Monica garden and dining room",
      visitMapEyebrow: "Santa Monica",
      visitMapDescription:
        "Dinner nightly, weekend brunch, valet, reservations, and private events.",
      atAGlanceTitle: "Fia Restaurant in Santa Monica.",
      atAGlanceDescription:
        "Fia Restaurant is a coastal Italian restaurant at 2454 Wilshire Boulevard in Santa Monica, CA 90403, with dinner Sunday through Thursday 5-9 PM, Friday and Saturday 5-10 PM, weekend brunch Saturday and Sunday 10:30 AM-4:30 PM, external reservations, Toast ordering, and private dining.",
      atAGlancePhoneLabel:
        "for reservation assistance, dinner questions, and event handoff",
      faqItems: [
        {
          question: "Where is Fia Restaurant?",
          answer:
            "Fia Restaurant is at 2454 Wilshire Boulevard, Santa Monica, CA 90403.",
        },
        {
          question: "How do I reserve at Fia?",
          answer:
            "Use Fia's external reservation flow from the website, or call the restaurant for reservation assistance.",
        },
        {
          question: "Does Fia offer pickup or delivery?",
          answer:
            "Yes. Pickup and delivery route through Toast; Fia does not use the Tuscan Cove first-party cart.",
        },
        {
          question: "What does Fia serve?",
          answer:
            "Fia serves coastal Italian dinner, weekend brunch, shared starters, raw fish, pasta, seafood, mains, desserts, cocktails, and wine.",
        },
      ],
    };
  }

  return {
    brand: "tuscan-cove",
    metadataTitle: "Tuscan Cove Bar & Patio | Southern Highlands, Las Vegas",
    metadataDescription:
      "Your neighborhood bar & restaurant inside Tuscan Highlands—open to everyone. Gate code 418. Pizza, burgers, Sunday brunch, and happy hour daily 4–6 PM.",
    keywords: [
      "order Tuscan Cove Las Vegas",
      "happy hour Southern Highlands",
      "Tuscan Highlands apartment restaurant",
      "Tuscan Cove Bar and Patio",
    ],
    mobileBrandLabel: "Tuscan Cove Bar & Patio",
    mobileHeroTitle: "Pizza, drinks, and patio nights",
    mobileHeroDescription:
      "Your neighborhood bar & restaurant inside Tuscan Highlands—open to everyone. Call 418 at the gate box.",
    heroImageAlt:
      "Tuscan Cove patio with fire table and evening courtyard seating",
    desktopHeroImageAlt:
      "Tuscan Cove patio at night with the long communal fire table glowing",
    eyebrow: "Tuscan Cove Bar & Patio · Tuscan Highlands",
    heroTitle: "Southern Highlands bar & patio.",
    answerFirst:
      "Your neighborhood bar & restaurant inside Tuscan Highlands—open to everyone. Stone-fired pizza, cocktails, daily happy hour, Sunday brunch, and patio seating. Dial gate code 418 at the call box.",
    primaryCtaLabel: "Order Online",
    secondaryCtaLabel: "Reserve a table",
    heroSocialProof:
      "Southern Highlands regulars call out the patio, smashburger, pizza, cocktails, and easy neighborhood feel.",
    heroReserveAssurance:
      "Pickup and delivery direct · happy hour 4–6 daily · instant reservations",
    reserveReinforcement: {
      eyebrow: "Seen something you like?",
      title: "Order Tuscan Cove direct.",
      description:
        "Build a pickup or delivery order from the dishes you just saw. Staying for the patio? Reservations are still one click away.",
      ctaLabel: "Order online",
    },
    quickStats: [
      {
        label: "Daily happy hour",
        value: "4-6 PM",
      },
      {
        label: "Big screens",
        value: "Wings, pizza, bar seats",
      },
      {
        label: "Open to public",
        value: "Enter 418 to call Tuscan Cove",
      },
    ],
    photoEyebrow: "",
    photoTitle: "",
    photoMetaAddress: "12656 Southern Highlands Pkwy",
    photoMetaStatus: "Open to the public",
    quickStripMenuDescription: "Pizza, wings, brunch",
    directionsAddress: "12656 Southern Highlands Pkwy",
    groupsDescription: "Private events, catering, group tables",
    hoursQuickLabel: "Hours",
    hoursQuickValue: "Mon–Sat 4–9pm · Sun 10am–8pm · HH 4–6pm daily",
    phoneCtaLabel: "Call the Bar",
    phoneCtaDescription: `${formattedPhone} for patio, bar, and game-night questions.`,
    guestSectionEyebrow: "What guests keep noticing",
    guestSectionTitle: "The easy answer for where to go tonight.",
    guestSectionDescription:
      "A neighborhood room with enough polish for dinner and enough ease for the last-minute group text.",
    guestImageAlt: "Tuscan Cove bar seating with big screens",
    guestImageCaption:
      "Big screens, patio air, and drinks built for lingering.",
    guestSignals: [
      {
        label: "No Strip drive",
        value:
          "A polished bar-and-patio night inside the Tuscan Highlands apartment community.",
      },
      {
        label: "Game-night ready",
        value:
          "Big screens, wings, pizza, cocktails, and tables made for groups.",
      },
      {
        label: "Easy public access",
        value: "Not a resident? Enter 418 at the gate box to call Tuscan Cove.",
      },
    ],
    reviewsHeading: "What our guests are saying",
    reviewsHref: `https://www.google.com/search?q=${encodeURIComponent(
      `${location.name} ${location.address.line1} reviews`,
    )}`,
    featuredReviews: [
      {
        source: "Thomas Rocha",
        eyebrow: "5 star",
        meta: "May 2026",
        excerpt:
          "Amazing staff. Blake and the crew were wonderful, and the chef made our meal beyond delicious. I come here once a month for the food and the friendly service. Family friendly.",
      },
      {
        source: "Cindy Wireman",
        eyebrow: "5 star",
        meta: "Mar 2026",
        excerpt:
          "Pizza, fish and chips, wedge salad, shrimp cocktail. Everything hit. Real menu depth beyond typical bar food, and we're already planning our next visit.",
      },
      {
        source: "Sheeka Stewart",
        eyebrow: "5 star",
        meta: "Feb 2026",
        excerpt:
          "Mommy's day out at happy hour. Drinks and pizza were surprisingly great. Attentive service without being pushy, and the patio was the perfect afternoon call.",
      },
      {
        source: "Christopher McG",
        eyebrow: "5 star",
        meta: "Dec 2025",
        excerpt:
          "This place is amazing, a hidden gem. Food and drinks were great, ambiance on point. We stayed for another round and will be back with the rest of the family.",
      },
      {
        source: "Susan Shoffner",
        eyebrow: "5 star",
        meta: "Oct 2025",
        excerpt:
          "We were a little concerned because it's in a housing complex, but we were pleasantly surprised. Great food, great atmosphere, and the patio is worth the visit.",
      },
    ],
    happyHour: {
      eyebrow: "Daily 4-6 PM",
      title: "Happy hour before dinner, the game, or the patio.",
      description:
        "Start with cocktails, beer, wings, pizza, and shareables before the dinner rush or kickoff.",
      hours: "Daily 4-6 PM",
      imageAlt:
        "Tuscan Cove interior bar with big screens, sports memorabilia, and red velvet seating",
      imageEyebrow: "Happy Hour",
      imageDescription:
        "A simple reason to gather before the dinner rush, the first whistle, or a patio night.",
      details: [
        {
          label: "Game-day order",
          value: "Wings, pizza, cocktails, beer, and shareables",
        },
        {
          label: "Best seat",
          value: "Inside for the screens, outside for the patio",
        },
      ],
    },
    inlineOffer: {
      source: "cove_club",
      eyebrow: "Cove Club",
      heading:
        "Patio nights, happy hour, and game days. Hear about them first in Southern Highlands.",
      description:
        "Free to join. Get patio night invites, happy hour notes, game day reminders, and first access to member-only specials by email.",
      submitLabel: "Join Cove Club",
      privacyNote:
        "Email is used for Cove Club updates. Free to join. Unsubscribe anytime.",
      successEyebrow: "You are in Cove Club",
      successHeading: "Watch your inbox for Cove Club updates.",
      successDescription:
        "Your free membership is confirmed. Watch your inbox for patio nights, happy hour notes, event invitations, and member-only specials when they go live.",
    },
    menuPreviewDescription: "Dishes guests order again and again.",
    featureHeading: "Everything for an easy Southern Highlands night out.",
    featureBadges: [
      "Patio Seating",
      "Big Screens",
      "Daily Happy Hour",
      "Open to the Public",
      "Easy Parking",
    ],
    knownForItems: [
      "Pizza",
      "Wings",
      "Smash Burgers",
      "Cocktails",
      "Patio",
      "Brunch",
      "Happy Hour",
    ],
    orderEyebrow: "Pickup + delivery",
    orderHeading: "Order Tuscan Cove pickup or delivery.",
    orderDescription:
      "Pizza, wings, burgers, and dinner plates are available through first-party pickup and Uber Direct delivery.",
    orderCtaLabel: "Order Pickup or Delivery",
    localGuidesEyebrow: "Southern Highlands guides",
    localGuidesTitle: "Pick the plan that fits tonight.",
    localGuideLinks: [
      {
        href: "/best",
        label: "All Guides",
        ariaLabel: "Browse all Southern Highlands dining guides",
        description: "Browse every Southern Highlands dining guide",
      },
      {
        href: "/best/restaurants-southern-highlands-las-vegas",
        label: "Restaurants",
        ariaLabel: "Restaurants in Southern Highlands",
        description:
          "Dinner, drinks, brunch, and patio seating near the neighborhood.",
      },
      {
        href: "/best/happy-hour-southern-highlands-las-vegas",
        label: "Happy Hour",
        ariaLabel: "Happy Hour in Southern Highlands",
        description: "Daily 4-6 PM drinks and bites before dinner or the game.",
      },
      {
        href: "/best/brunch-southern-highlands-las-vegas",
        label: "Sunday Brunch",
        ariaLabel: "Sunday brunch in Southern Highlands",
        description:
          "Sunday brunch with patio seating, cocktails, and brunch plates.",
      },
      {
        href: "/best/pizza-southern-highlands-las-vegas",
        label: "Pizza",
        ariaLabel: "Pizza in Southern Highlands",
        description: "Stone-fired pizzas, wings, and shareables for the table.",
      },
      {
        href: "/best/burgers-southern-highlands-las-vegas",
        label: "Burgers",
        ariaLabel: "Burgers in Southern Highlands",
        description:
          "Smash burgers, bar seats, and game-night plates close to home.",
      },
      {
        href: "/best/game-day-restaurant-southern-highlands-las-vegas",
        label: "Game Day",
        ariaLabel: "Game day restaurant in Southern Highlands",
        description:
          "Big screens, bar seats, pizza, wings, and patio tables for sports watch parties.",
      },
      {
        href: "/best/restaurants-near-south-point-casino",
        label: "Near South Point",
        ariaLabel: "South Point restaurants off the casino floor",
        description:
          "South Point restaurants nearby—open to the public, pizza, happy hour, groups. Gate code 418.",
      },
      {
        href: "/best/private-dining-las-vegas",
        label: "Private Dining",
        ariaLabel: "Private dining in Las Vegas",
        description:
          "Group dinners, celebrations, and semi-private seating for events.",
      },
      {
        href: "/best/rehearsal-dinner-venue-las-vegas",
        label: "Rehearsal Dinners",
        ariaLabel: "Rehearsal dinner venue in Las Vegas",
        description:
          "Wedding-week meals with private dining, group menus, and catering trays.",
      },
      {
        href: "/best/corporate-catering-las-vegas",
        label: "Corporate Catering",
        ariaLabel: "Corporate catering in Las Vegas",
        description:
          "Office lunches, team dinners, and business catering across the valley.",
      },
      {
        href: "/best/catering-southern-highlands-las-vegas",
        label: "Catering",
        ariaLabel: "Catering in Southern Highlands",
        description:
          "Platters and entrees catered for homes, offices, and gatherings.",
      },
      {
        href: "/best/directions-gate-code-southern-highlands-las-vegas",
        label: "Directions",
        ariaLabel: "Tuscan Cove directions and gate code in Southern Highlands",
        description:
          "Address, gate code 418, parking route, and arrival tips for first-time visits.",
      },
    ],
    visitEyebrow: "First-time visit",
    visitTitle: "Find Tuscan Cove inside Tuscan Highlands.",
    visitGateCode: "418",
    visitDirectionsCtaLabel: "Open in Maps",
    visitSignAlt: "Tuscan Cove property entrance sign at Tuscan Highlands",
    visitSignCaption: "Main entrance on Southern Highlands Pkwy.",
    visitMapAlt:
      "Aerial wayfinding map showing the route from Southern Highlands Pkwy through the Tuscan Highlands main entrance (gate code 418) to Tuscan Cove parking",
    visitMapEyebrow: "Parking route",
    visitMapDescription:
      "Dial gate code 418 at the call box, then follow the orange route to Tuscan Cove parking.",
    atAGlanceTitle: "Tuscan Cove Bar & Patio inside Tuscan Highlands",
    atAGlanceDescription:
      "Tuscan Cove Bar & Patio is a neighborhood restaurant inside the Tuscan Highlands apartment complex in Southern Highlands, Las Vegas, at 12656 Southern Highlands Pkwy, Las Vegas, NV 89141. The kitchen and bar pour stone-fired pizza, burgers, salads, craft cocktails, Sunday brunch, and daily happy hour from 4 to 6 PM.",
    atAGlancePhoneLabel:
      "for reservations, patio seating, or game-night questions",
    faqItems: [
      {
        question: "What are you known for?",
        answer:
          "We are best known for our pizza, wings, smash burgers, cocktails, patio, brunch, and happy hour.",
      },
      {
        question: "What meals do you serve?",
        answer:
          "We serve dinner Monday through Saturday from 4 to 9 PM and Sunday brunch from 10 AM to 8 PM. Happy hour runs daily from 4 to 6 PM.",
      },
      {
        question: "Can I order online for pickup or delivery?",
        answer:
          "Yes. You can order online for pickup or delivery directly through our website.",
      },
      {
        question: "Where are you located and how do I get in?",
        answer: `Tuscan Cove Bar & Patio is at 12656 Southern Highlands Pkwy, Las Vegas, NV 89141, inside the Tuscan Highlands community. Dial gate code 418 at the call box and follow the signs to Tuscan Cove parking. Call ${formattedPhone} if you need help.`,
      },
    ],
  };
}
